import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import { tx } from '../../lib/adminI18n';

// サマリー 年間ビュー   設計: .plan/admin-statistics-impl-v1.1.md §12
//   暦年（初期表示）／年度（4月〜翌3月）。施設の絞り込みのみ。予約確定のデータだけ。
type Num = number | null;
interface Month {
  ym: string; reservation_count: number; nights: number; occupied_days: number; available_days: number;
  occupancy_rate: Num; avg_nights: Num; cancel_count: number; cancel_rate: Num; coupon_usage_count: number; cleaning_done: number;
}
interface YearData {
  period: { basis: 'calendar' | 'fiscal'; year: number; from: string; to: string };
  months: Month[]; totals: Omit<Month, 'ym'>;
  property_month: { property_id: string; name: string; abbreviation: string | null; rates: Num[]; year_rate: Num }[];
  by_channel: { channel: string; reservation_count: number; nights: number; share_count: Num; share_nights: Num }[];
  by_coupon: { code: string; type: string; usage_count: number }[];
  by_cleaner: { cleaner_id: number | null; name: string | null; done_count: number; avg_minutes: Num }[];
}
const pct = (v: Num) => (v == null ? '—' : `${v.toFixed(1)}%`);
const dash = (v: Num) => (v == null ? '—' : String(v));
const mLabel = (ym: string) => `${Number(ym.slice(5, 7))}${tx('sumY.monthUnit')}`;
function heat(v: Num): CSSProperties {
  if (v == null || v <= 0) return { background: '#f8fafc', color: '#9ca3af' };
  const a = Math.min(1, v / 100);
  return { background: `rgba(37, 99, 235, ${0.12 + a * 0.75})`, color: a > 0.55 ? '#fff' : '#1e3a8a', fontWeight: 600 };
}
function Bar({ v }: { v: Num }) {
  const w = v == null ? 0 : Math.max(0, Math.min(100, v));
  return <span className="sum-bar" aria-hidden="true"><span style={{ width: `${w}%` }} /></span>;
}

export default function SummaryYear({ year, basis, pid, props, onChange }: {
  year: number; basis: 'calendar' | 'fiscal'; pid: string; props: { id: string; name: string }[];
  onChange: (v: { year: number; basis: 'calendar' | 'fiscal'; pid: string }) => void;
}) {
  const [y, setY] = useState(year);
  const [b, setB] = useState(basis);
  const [p, setP] = useState(pid);
  const [data, setData] = useState<YearData | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = useCallback(async (q: { year: number; basis: 'calendar' | 'fiscal'; pid: string }) => {
    setLoading(true); setErr(null); onChange(q);
    try {
      const qs = new URLSearchParams({ year: String(q.year), basis: q.basis, property_id: q.pid });
      const res = await fetch(`/api/admin/summary/year?${qs}`, { headers: adminHeaders(), credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || tx('sum.loadErr')); setData(null); return; }
      setData(d as YearData);
    } catch { setErr(tx('sum.loadErr')); }
    finally { setLoading(false); }
  }, [onChange]);
  useEffect(() => { run({ year: y, basis: b, pid: p }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (d: number) => { const n = y + d; setY(n); run({ year: n, basis: b, pid: p }); };
  const chName = (c: string) => tx(`sum.ch.${c}` as any);
  const t = data?.totals;

  return (
    <>
      <div className="sum-filters">
        <div className="sum-month">
          <button type="button" className="sum-nav" onClick={() => go(-1)} disabled={loading} aria-label={tx('sumY.prev')}>‹</button>
          <span className="sum-year-label">{b === 'fiscal' ? tx('sumY.fiscalLabel', { y }) : tx('sumY.calendarLabel', { y })}</span>
          <button type="button" className="sum-nav" onClick={() => go(1)} disabled={loading} aria-label={tx('sumY.next')}>›</button>
        </div>
        <div className="sum-seg" role="group" aria-label={tx('sumY.basis')}>
          <button type="button" className={b === 'calendar' ? 'active' : ''} onClick={() => { setB('calendar'); run({ year: y, basis: 'calendar', pid: p }); }}>{tx('sumY.calendar')}</button>
          <button type="button" className={b === 'fiscal' ? 'active' : ''} onClick={() => { setB('fiscal'); run({ year: y, basis: 'fiscal', pid: p }); }}>{tx('sumY.fiscal')}</button>
        </div>
        <label className="sum-field">
          <span>{tx('sum.property')}</span>
          <select className="sum-input" value={p} onChange={(e) => setP(e.target.value)}>
            <option value="ALL">{tx('sum.allProps')}</option>
            {props.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <button type="button" className="sum-btn" onClick={() => run({ year: y, basis: b, pid: p })} disabled={loading}>
          {loading ? tx('sum.loading') : tx('sum.run')}
        </button>
      </div>
      {data && <p className="sum-period">{tx('sum.period', { from: data.period.from, to: data.period.to })}</p>}
      {err && <div className="sum-error" role="alert">{err}</div>}

      {data && t && (<>
        <div className="sum-cards">
          <div className="sum-card"><div className="sum-card-label">{tx('sum.reservations')}</div><div className="sum-card-value">{t.reservation_count}<small>{tx('sum.unitCount')}</small></div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.nights')}</div><div className="sum-card-value">{t.nights}<small>{tx('sum.unitNight')}</small></div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.occupancy')}</div><div className="sum-card-value">{pct(t.occupancy_rate)}</div><div className="sum-card-note">{tx('sum.noteOcc', { a: t.occupied_days, b: t.available_days })}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.cancelRate')}</div><div className="sum-card-value">{pct(t.cancel_rate)}</div><div className="sum-card-note">{tx('sum.noteCancel', { n: t.cancel_count })}</div></div>
        </div>

        {/* 月別推移 */}
        <section className="sum-section">
          <h2>{tx('sumY.monthly')}</h2>
          <div className="sum-table-wrap">
            <table className="sum-table">
              <thead><tr>
                <th>{tx('sumY.month')}</th><th>{tx('sum.reservations')}</th><th>{tx('sum.nights')}</th><th>{tx('sum.occupancy')}</th>
                <th>{tx('sum.avgNights')}</th><th>{tx('sum.cancel')}</th><th>{tx('sum.coupon')}</th><th>{tx('sumY.cleaningDone')}</th>
              </tr></thead>
              <tbody>
                {data.months.map((m) => (
                  <tr key={m.ym} className={m.reservation_count === 0 && m.nights === 0 ? 'sum-zero' : ''}>
                    <td>{m.ym}</td><td>{m.reservation_count}</td><td>{m.nights}</td>
                    <td className="sum-occ"><Bar v={m.occupancy_rate} />{pct(m.occupancy_rate)}</td>
                    <td>{dash(m.avg_nights)}</td><td>{m.cancel_count}<small>（{pct(m.cancel_rate)}）</small></td>
                    <td>{m.coupon_usage_count}</td><td>{m.cleaning_done}</td>
                  </tr>
                ))}
                <tr className="sum-total">
                  <td>{tx('sum.total')}</td><td>{t.reservation_count}</td><td>{t.nights}</td>
                  <td className="sum-occ"><Bar v={t.occupancy_rate} />{pct(t.occupancy_rate)}</td>
                  <td>{dash(t.avg_nights)}</td><td>{t.cancel_count}<small>（{pct(t.cancel_rate)}）</small></td>
                  <td>{t.coupon_usage_count}</td><td>{t.cleaning_done}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="sum-note">{tx('sumY.noteMonthly')}</p>
        </section>

        {/* 施設 × 月の稼働率 */}
        <section className="sum-section">
          <h2>{tx('sumY.heatmap')}</h2>
          <div className="sum-table-wrap">
            <table className="sum-table sum-heat">
              <thead><tr><th>{tx('sum.property')}</th>{data.months.map((m) => <th key={m.ym}>{mLabel(m.ym)}</th>)}<th>{tx('sumY.year')}</th></tr></thead>
              <tbody>
                {data.property_month.map((r) => (
                  <tr key={r.property_id}>
                    <td>{r.abbreviation || r.name}</td>
                    {r.rates.map((v, i) => <td key={i} style={heat(v)}>{v == null || v === 0 ? '—' : Math.round(v)}</td>)}
                    <td style={heat(r.year_rate)}>{pct(r.year_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sum-note">{tx('sumY.noteHeat')}</p>
        </section>

        {/* 経路別構成 */}
        <section className="sum-section">
          <h2>{tx('sumY.channelShare')}</h2>
          <div className="sum-table-wrap">
            <table className="sum-table">
              <thead><tr><th>{tx('sum.channel')}</th><th>{tx('sum.reservations')}</th><th>{tx('sumY.share')}</th><th>{tx('sum.nights')}</th><th>{tx('sumY.share')}</th></tr></thead>
              <tbody>
                {data.by_channel.map((r) => (
                  <tr key={r.channel} className={r.reservation_count === 0 && r.nights === 0 ? 'sum-zero' : ''}>
                    <td>{chName(r.channel)}</td>
                    <td>{r.reservation_count}</td><td className="sum-occ"><Bar v={r.share_count} />{pct(r.share_count)}</td>
                    <td>{r.nights}</td><td className="sum-occ"><Bar v={r.share_nights} />{pct(r.share_nights)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="sum-two">
          <section className="sum-section">
            <h2>{tx('sum.byCoupon')}</h2>
            {data.by_coupon.length === 0 ? <p className="sum-empty">{tx('sumY.noCoupon')}</p> : (
              <table className="sum-table">
                <thead><tr><th>{tx('sum.code')}</th><th>{tx('sum.type')}</th><th>{tx('sum.usage')}</th></tr></thead>
                <tbody>
                  {data.by_coupon.map((r) => <tr key={r.code}><td><code>{r.code}</code></td><td>{r.type === 'campaign' ? tx('sum.campaign') : tx('sum.repeater')}</td><td>{r.usage_count}</td></tr>)}
                  <tr className="sum-total"><td>{tx('sum.total')}</td><td /><td>{data.by_coupon.reduce((a, r) => a + r.usage_count, 0)}</td></tr>
                </tbody>
              </table>
            )}
          </section>
          <section className="sum-section">
            <h2>{tx('sum.byCleaner')}</h2>
            {data.by_cleaner.length === 0 ? <p className="sum-empty">{tx('sumY.noCleaning')}</p> : (
              <table className="sum-table">
                <thead><tr><th>{tx('sum.cleaner')}</th><th>{tx('sum.done')}</th><th>{tx('sum.avgTime')}</th></tr></thead>
                <tbody>
                  {data.by_cleaner.map((r) => (
                    <tr key={String(r.cleaner_id)}>
                      <td>{r.cleaner_id == null ? <span className="sum-muted">{tx('sum.unassigned')}</span> : r.name}</td>
                      <td>{r.done_count}</td><td>{r.avg_minutes == null ? '—' : tx('sum.avgMin', { n: r.avg_minutes })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
        <p className="sum-note">{tx('sum.noteConfirmedOnly')}</p>
      </>)}
    </>
  );
}
